import React, { useState, useEffect } from "react";
import { apiCall } from "../config/api";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Snackbar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

const CampaignDetailPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pins, setPins] = useState([]);
  const [filteredPins, setFilteredPins] = useState([]);
  const [pinsLoading, setPinsLoading] = useState(true);
  const [pinsError, setPinsError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableCampaign, setEditableCampaign] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Filtreleme state'leri
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [availableCategories, setAvailableCategories] = useState([]);

  useEffect(() => {
    const fetchCampaign = async () => {
      setLoading(true);
      try {
        const data = await apiCall(`/api/campaigns/${campaignId}`, {
          method: "GET",
        });

        if (data.success) {
          setCampaign(data.campaign);
          setEditableCampaign(data.campaign);
        } else {
          throw new Error(data.error || "Kampanya verisi alınamadı.");
        }
      } catch (error) {
        console.error("Error fetching campaign details:", error);
        setCampaign(null); // Hata durumunda kampanyayı temizle
      } finally {
        setLoading(false);
      }
    };

    const fetchCampaignPins = async () => {
      try {
        setPinsLoading(true);
        const data = await apiCall(`/api/campaigns/${campaignId}/pins`, {
          method: "GET",
        });
        if (data.success) {
          const fetchedPins = data.pins || [];
          setPins(fetchedPins);
          setFilteredPins(fetchedPins);

          // Mevcut kategorileri çıkar
          const categories = [
            ...new Set(
              fetchedPins.map((pin) => pin.category || "Belirtilmemiş")
            ),
          ];
          setAvailableCategories(categories);

          setPinsError(null);
        } else {
          throw new Error(data.error || "Pinler yüklenemedi.");
        }
      } catch (err) {
        setPinsError(err.message);
        console.error("Error fetching campaign pins:", err);
      } finally {
        setPinsLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
      fetchCampaignPins();
    }
  }, [campaignId]);

  // Filtreleme fonksiyonu
  useEffect(() => {
    let filtered = [...pins];

    // Arama filtresi
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (pin) =>
          pin.name?.toLowerCase().includes(term) ||
          pin.address?.toLowerCase().includes(term) ||
          pin.id?.toLowerCase().includes(term)
      );
    }

    // Kategori filtresi
    if (categoryFilter !== "all") {
      filtered = filtered.filter(
        (pin) => (pin.category || "Belirtilmemiş") === categoryFilter
      );
    }

    setFilteredPins(filtered);
  }, [pins, searchTerm, categoryFilter]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryChange = (event) => {
    setCategoryFilter(event.target.value);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableCampaign(campaign);
  };

  const handleSave = async () => {
    try {
      const updatedData = await apiCall(`/api/campaigns/${campaignId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ campaignName: editableCampaign.campaignName }),
      });

      if (!updatedData.success) {
        throw new Error(updatedData.error || "Kampanya güncellenemedi.");
      }
      setCampaign(updatedData.campaign);
      setEditableCampaign(updatedData.campaign);
      setIsEditing(false);
      setSnackbar({
        open: true,
        message: "Kampanya başarıyla güncellendi!",
        severity: "success",
      });
    } catch (error) {
      console.error("Error updating campaign:", error);
      setSnackbar({
        open: true,
        message: `Hata: ${error.message}`,
        severity: "error",
      });
    }
  };

  const handleInputChange = (e) => {
    if (!editableCampaign) return;
    const { name, value } = e.target;
    setEditableCampaign((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeletePin = async (pinId) => {
    if (
      window.confirm(
        "Bu pini kalıcı olarak silmek istediğinizden emin misiniz?"
      )
    ) {
      try {
        const data = await apiCall(`/api/pins/${pinId}`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ campaignId }),
        });

        if (!data.success) {
          throw new Error(data.error || "Pin silinirken bir hata oluştu.");
        }

        setPins((prevPins) => prevPins.filter((pin) => pin.id !== pinId));
        setSnackbar({
          open: true,
          message: "Pin başarıyla silindi.",
          severity: "success",
        });
      } catch (error) {
        console.error("Pin silme hatası:", error);
        setSnackbar({
          open: true,
          message: `Hata: ${error.message}`,
          severity: "error",
        });
      }
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!campaign) {
    return (
      <Box sx={{ textAlign: "center", mt: 4 }}>
        <Typography variant="h6">Kampanya bulunamadı.</Typography>
        <Button variant="contained" onClick={() => navigate("/campaigns")}>
          Geri Dön
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/campaigns")}
        sx={{ mb: 2 }}
      >
        Kampanyalara Geri Dön
      </Button>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          {isEditing ? (
            <TextField
              name="name"
              label="Kampanya Adı"
              value={editableCampaign?.name || ""}
              onChange={handleInputChange}
              variant="outlined"
              size="small"
              sx={{ flexGrow: 1, mr: 2 }}
            />
          ) : (
            <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0 }}>
              {campaign.name}
            </Typography>
          )}

          <Box>
            {isEditing ? (
              <>
                <IconButton onClick={handleSave} color="primary">
                  <SaveIcon />
                </IconButton>
                <IconButton onClick={handleCancel} color="secondary">
                  <CancelIcon />
                </IconButton>
              </>
            ) : (
              <IconButton onClick={handleEdit} color="primary">
                <EditIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">
              <strong>ID:</strong> {campaign.id}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">
              <strong>Durum:</strong>
              <Chip
                label={campaign.status}
                color={campaign.status === "completed" ? "success" : "default"}
                size="small"
                sx={{ ml: 1 }}
              />
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">
              <strong>Oluşturulma:</strong>{" "}
              {new Date(campaign.createdAt).toLocaleString("tr-TR")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1">
              <strong>Güncellenme:</strong>{" "}
              {new Date(campaign.updatedAt).toLocaleString("tr-TR")}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              <strong>Pin Sayısı:</strong> {pins.length}
            </Typography>
          </Grid>
          {isEditing && (
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Açıklama"
                value={editableCampaign?.description || ""}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
                variant="outlined"
                sx={{ mt: 2 }}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h5">Kampanyadaki Pinler</Typography>
          <Chip
            label={`${filteredPins.length} / ${pins.length} pin`}
            color="primary"
            variant="outlined"
          />
        </Box>

        {/* Filtreleme Alanı */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            placeholder="Pin adı, adres veya ID ara..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm("")}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 300 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={categoryFilter}
              label="Kategori"
              onChange={handleCategoryChange}
            >
              <MenuItem value="all">Tüm Kategoriler</MenuItem>
              {availableCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(searchTerm || categoryFilter !== "all") && (
            <Button
              size="small"
              variant="outlined"
              onClick={clearFilters}
              startIcon={<ClearIcon />}
            >
              Filtreleri Temizle
            </Button>
          )}
        </Box>

        {pinsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 200,
            }}
          >
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Pinler yükleniyor...</Typography>
          </Box>
        ) : pinsError ? (
          <Alert severity="error">{pinsError}</Alert>
        ) : filteredPins.length > 0 ? (
          <List>
            {filteredPins.map((pin) => (
              <Card key={pin.id} variant="outlined" sx={{ mb: 2 }}>
                <CardContent>
                  <ListItem
                    secondaryAction={
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeletePin(pin.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={pin.name}
                      secondary={
                        <>
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {pin.address}
                          </Typography>
                          <br />
                          ID: {pin.id} | Kategori:{" "}
                          {pin.category || "Belirtilmemiş"}
                        </>
                      }
                    />
                  </ListItem>
                </CardContent>
              </Card>
            ))}
          </List>
        ) : searchTerm || categoryFilter !== "all" ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Arama kriterlerinize uygun pin bulunamadı.
            </Typography>
            <Button variant="outlined" onClick={clearFilters} sx={{ mt: 1 }}>
              Filtreleri Temizle
            </Button>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Bu kampanyaya ait pin bulunmuyor.
          </Typography>
        )}
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          onClick={() => navigate("/bulk-pin-creation")}
          sx={{ mt: 2 }}
        >
          Yeni Pin Ekle
        </Button>
      </Paper>
    </Box>
  );
};

export default CampaignDetailPage;
